/**
 * Client for the Network Releases REST endpoints (immutable snapshot history).
 *
 *   GET  /networks/{id}/releases                 → list summaries (newest first)
 *   GET  /networks/{id}/releases/{version}       → full snapshot
 *
 * These back the Version History UI in the workbench Settings tab.
 */
import { getRestApiBearerToken } from './apiToken';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

export interface NetworkReleaseSummary {
  networkId: string;
  releaseVersion: number;
  releasedAt: string;
  releasedBy: string;
  tenantId: string;
  supervisorSubs: string[];
  /** Published AWS SFN Version. Drives the ``MAJOR.MINOR`` UI label. */
  stateMachineVersion?: number;
  stateMachineArnVersioned?: string;
}

export interface NetworkReleaseDetail extends NetworkReleaseSummary {
  snapshot: Record<string, unknown>;
}

function requireToken(): string {
  const token = getRestApiBearerToken();
  if (!token) throw new Error('Not authenticated');
  return token;
}

function baseUrl(): string {
  return API_BASE_URL.replace(/\/$/, '');
}

export async function listNetworkReleases(
  networkId: string,
  opts?: { limit?: number },
): Promise<{ networkId: string; releases: NetworkReleaseSummary[]; count: number }> {
  const token = requireToken();
  const params = new URLSearchParams();
  if (opts?.limit != null) params.set('limit', String(opts.limit));
  const q = params.toString();
  const url = `${baseUrl()}/networks/${encodeURIComponent(networkId)}/releases${q ? `?${q}` : ''}`;
  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || `HTTP ${res.status}`);
  }
  const data = (await res.json()) as {
    networkId?: string;
    releases?: NetworkReleaseSummary[];
    count?: number;
  };
  return {
    networkId: data.networkId ?? networkId,
    releases: Array.isArray(data.releases) ? data.releases : [],
    count: typeof data.count === 'number' ? data.count : (data.releases?.length ?? 0),
  };
}

export async function getNetworkRelease(
  networkId: string,
  releaseVersion: number,
): Promise<NetworkReleaseDetail> {
  const token = requireToken();
  const v = Math.max(1, Math.floor(Number(releaseVersion) || 0));
  const url = `${baseUrl()}/networks/${encodeURIComponent(networkId)}/releases/${v}`;
  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || `HTTP ${res.status}`);
  }
  return (await res.json()) as NetworkReleaseDetail;
}

export interface NextReleaseLabelResponse {
  label: string;
  // 'MANIFEST' → a generative (manifest) network: no SFN version; the label is the
  // network's current manifest version (versioning happens at approval, not release-cut).
  kind: 'FIRST' | 'MAJOR' | 'MINOR' | 'LEGACY' | 'NOOP' | 'MANIFEST';
  stateMachineVersion?: number;
  nextReleaseVersion?: number;
}

export async function getNextReleaseLabel(
  networkId: string,
): Promise<NextReleaseLabelResponse> {
  const token = requireToken();
  const url = `${baseUrl()}/networks/${encodeURIComponent(networkId)}/next-release-label`;
  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || `HTTP ${res.status}`);
  }
  return (await res.json()) as NextReleaseLabelResponse;
}

/* ------------------------------------------------------------------------- *
 * Per-pool upgrade endpoint — retargets a pool's SFN alias to a different
 * released version.
 * ------------------------------------------------------------------------- */

export interface UpgradePoolResponse {
  pool: Record<string, unknown>;
  fromReleaseVersion: number;
  toReleaseVersion: number;
  stateMachineVersion: number;
  aliasArn: string;
}

export async function upgradePoolRelease(
  networkId: string,
  poolId: string,
  targetReleaseVersion: number,
  reason?: string,
): Promise<UpgradePoolResponse> {
  const token = requireToken();
  const url = `${baseUrl()}/networks/${encodeURIComponent(networkId)}/pools/${encodeURIComponent(poolId)}/upgrade`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      targetReleaseVersion,
      ...(reason ? { reason } : {}),
    }),
  });
  if (!res.ok) {
    const t = await res.text();
    let parsed: { error?: string; message?: string } = {};
    try { parsed = JSON.parse(t); } catch { /* ignore */ }
    const msg = parsed.message || parsed.error || t || `HTTP ${res.status}`;
    const e = new Error(msg) as Error & { code?: string; status?: number };
    e.code = parsed.error;
    e.status = res.status;
    throw e;
  }
  return (await res.json()) as UpgradePoolResponse;
}
